import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../services/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useTransactions } from '../../hooks/useTransactions'
import { Table } from '../Common/Table'
import { Pagination } from '../Common/Pagination'
import { Button } from '../Common/Button'
import { TransactionForm } from './TransactionForm'
import { ImportBatchesTab } from './ImportBatchesTab'
import { PolicyLinkedBadge } from '../Common/PolicyLinkedBadge'

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000)
    return () => clearTimeout(t)
  }, [onDismiss])
  return (
    <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded shadow-lg text-white text-sm font-semibold flex items-center gap-2 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
      {type === 'success' ? '✓' : '✗'} {message}
    </div>
  )
}

const fmtR = (val) => {
  if (val === null || val === undefined || val === '') return '-'
  return `R${Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const fmtDate = (val) => {
  if (!val) return '-'
  const d = new Date(val)
  if (isNaN(d)) return val
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
}

const fmtTime = (val) => {
  if (!val) return '-'
  const d = new Date(val)
  if (isNaN(d)) return '-'
  return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
}

export function CommissionsList({ sidebarCollapsed, isDarkMode = false }) {
  const { canAccess } = useAuth()
  const [activeTab, setActiveTab] = useState('transactions') // 'transactions' | 'batches'
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('transaction_date:desc')
  const [showForm, setShowForm] = useState(false)
  const [editingTx, setEditingTx] = useState(null)
  // Advisor filter
  const [advisors, setAdvisors] = useState([])
  const [selectedAdvisor, setSelectedAdvisor] = useState(null)

  // Import state
  const [importStage, setImportStage] = useState(null) // null | 'preview' | 'dup_batch' | 'importing'
  const [importRows, setImportRows] = useState([])
  const [dupBatch, setDupBatch] = useState(null) // existing duplicate batch info

  const fileRef = useRef()

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportScope, setExportScope] = useState('all')

  // Toast
  const [toast, setToast] = useState(null)

  const [sortBy, sortOrder] = sort.split(':')
  const { transactions, total, loading, createTransaction, updateTransaction, toggleActive,
    checkDuplicateBatch, fetchBatches, importTransactions, deleteBatches, clearAllTransactions,
    relinkUnlinked } =
    useTransactions(page, 100, search, sortBy, sortOrder, selectedAdvisor?.id || null)

  useEffect(() => {
    supabase.from('advisors').select('id, name, full_name').eq('active', true).order('name')
      .then(({ data }) => setAdvisors(data || []))
  }, [])

  const advisorName = (a) => a?.full_name || a?.name || 'Unnamed'

  const handleSave = async (formData) => {
    if (editingTx) {
      await updateTransaction(editingTx.id, formData)
    } else {
      await createTransaction(formData)
    }
    setShowForm(false)
    setEditingTx(null)
  }

  // ── IMPORT ──────────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    const XLSX = await import('xlsx')
    const buffer = await file.arrayBuffer()
    const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
    const ws = wb.Sheets[wb.SheetNames[0]]

    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:Z1000')

    let headerRowNum = 0
    let bestHeaderRow = { rowNum: 0, score: 0 }
    const specificPatterns = ['statement date', 'policy', 'invoice', 'client', 'product']
    const generalPatterns = ['date', 'adviser', 'split', 'supplier', 'amount']

    for (let rowNum = range.s.r; rowNum <= range.e.r && rowNum < 30; rowNum++) {
      const rowCells = []
      for (let colNum = range.s.c; colNum <= Math.min(range.e.c, colNum + 12); colNum++) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum })
        const cell = ws[cellAddress]
        const cellValue = (cell?.v || '').toString().trim()
        rowCells.push(cellValue.toLowerCase())
      }

      const nonEmptyCells = rowCells.filter(c => c.length > 0).length
      const specificMatches = rowCells.filter(c => specificPatterns.some(p => c.includes(p))).length
      const generalMatches = rowCells.filter(c => generalPatterns.some(p => c.includes(p))).length
      const score = specificMatches * 5 + generalMatches + (nonEmptyCells * 0.5)

      if (nonEmptyCells >= 8 && specificMatches >= 2 && score > bestHeaderRow.score) {
        bestHeaderRow = { rowNum, score }
      }
    }

    headerRowNum = bestHeaderRow.rowNum || 0

    const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '', range: headerRowNum })

    if (rawRows.length > 0) {
      console.log('📋 Detected headers:', Object.keys(rawRows[0]))
    }

    const findCol = (obj, patterns) => {
      for (const p of patterns) {
        if (obj[p] !== undefined && obj[p] !== null && obj[p] !== '') return obj[p]
      }
      return null
    }

    const mapped = rawRows.map(r => ({
      // Remittance Date (new name) or Transaction Date (old name)
      transaction_date: findCol(r, ['Remittance Date', 'Transaction Date', 'Tx Date']) ? formatExcelDate(findCol(r, ['Remittance Date', 'Transaction Date', 'Tx Date'])) : null,
      statement_date: findCol(r, ['Statement Date', 'Stmt Date', 'Date']) ? formatExcelDate(findCol(r, ['Statement Date', 'Stmt Date', 'Date'])) : null,
      policy_invoice_no: String(findCol(r, ['Policy Invoice No.', 'Policy Invoice No', 'Policy/Invoice No.', 'Policy/Invoice No', 'Invoice No', 'Policy Number']) || '').trim() || null,
      policy_number: String(findCol(r, ['Policy', 'Policy Number', 'Policy ID']) || '').trim() || null,
      client: String(findCol(r, ['Client Imported', 'Client', 'Customer', 'Name']) || '').trim() || null,
      product: String(findCol(r, ['Product Imported', 'Product', 'Product Type']) || '').trim() || null,
      supplier: String(findCol(r, ['Supplier', 'Supplier Name']) || '').trim() || null,
      advisor_split: parsePct(findCol(r, ['Adviser Split', '(%)*', 'Advisor Split', 'Adviser Split %', 'Split %'])),
      supplier_amount_excl: parseNum(findCol(r, ['Supplier Amount Excl', 'Supplier Amount Excl.', 'Base Value'])),
      supplier_amount_vat: parseNum(findCol(r, ['Supplier Amount VAT', 'Supplier Amount VAT.', 'VAT'])),
      advisor_share_excl: parseNum(findCol(r, ['Adviser Share Excl', 'Adviser Share Excl.', '(ex VAT)', 'Advisor Share Excl', 'Advisor Share Excl.'])),
      advisor_share_vat: parseNum(findCol(r, ['Advisor Share VAT', 'Adviser Share VAT', 'Adviser Share VAT.', 'Advisor Share VAT.'])),
      advisor_share_inc: parseNum(findCol(r, ['Adviser Share Incl', 'Adviser Share Inc', 'Adviser Share Inc.', '(incl VAT)', 'Advisor Share Inc', 'Advisor Share Inc.'])),
      active: true,
    }))

    setImportRows(mapped)
    setImportStage('preview')
  }

  const formatExcelDate = (val) => {
    if (!val) return null
    if (val instanceof Date) return val.toISOString().slice(0, 10)
    const s = String(val).trim()
    const d = new Date(s)
    if (!isNaN(d)) return d.toISOString().slice(0, 10)
    return s
  }

  const parseNum = (val) => {
    if (val === '' || val === null || val === undefined) return null
    const n = parseFloat(String(val).replace(/[^0-9.-]/g, ''))
    return isNaN(n) ? null : n
  }

  const parsePct = (val) => {
    const n = parseNum(val)
    if (n === null) return null
    return (n > 0 && n < 1) ? parseFloat((n * 100).toFixed(4)) : n
  }

  const normPct = (v) => {
    const n = Number(v)
    if (!n) return null
    return (n > 0 && n < 1) ? n * 100 : n
  }

  // Called when user clicks "Import" on preview — check for duplicate batch first
  const handleConfirmImport = async () => {
    if (importStage === 'checking' || importStage === 'importing') return
    setImportStage('checking')
    const today = new Date().toISOString().split('T')[0]
    const totalAdvisorExcl = Math.round(importRows.reduce((s, r) => s + (r.advisor_share_excl || 0), 0) * 100) / 100

    const existing = await checkDuplicateBatch(today, importRows.length, totalAdvisorExcl)
    if (existing) {
      setDupBatch(existing)
      setImportStage('dup_batch')
      return
    }
    await doImport()
  }

  const doImport = async () => {
    setImportStage('importing')
    try {
      const { inserted } = await importTransactions(importRows)
      setImportStage(null)
      setImportRows([])
      setDupBatch(null)
      setToast({ message: `Imported ${inserted} transactions.`, type: 'success' })
    } catch (err) {
      setImportStage(null)
      setToast({ message: `Import failed: ${err.message}`, type: 'error' })
    }
  }

  // ── TEMPLATE DOWNLOAD ──────────────────────────────────────────────────────
  const downloadTemplate = async () => {
    const XLSX = await import('xlsx')
    const templateData = [
      {
        'Remittance Date': '2026-04-01',
        'Statement Date': '2026-04-01',
        'Policy Invoice No.': 'POL001',
        'Client Imported': 'Client Name',
        'Product Imported': 'Insurance Product',
        'Base Value': '',
        'Adviser Split': '18%',
        'Supplier Amount Excl': '10000.00',
        'Supplier Amount VAT': '1500.00',
        'Adviser Share Excl': '5000.00',
        'Advisor Share VAT': '750.00',
        'Adviser Share Incl': '5750.00',
      }
    ]

    const ws = XLSX.utils.json_to_sheet(templateData)

    const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'E6A83C' } } }
    const range = XLSX.utils.decode_range(ws['!ref'])
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })]
      if (cell) cell.s = headerStyle
    }

    ws['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 22 }, { wch: 22 },
      { wch: 12 }, { wch: 13 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
    ]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Policy Transactions')
    XLSX.writeFile(wb, 'PolicyTransactions_Template.xlsx')
    setToast({ message: 'Template downloaded', type: 'success' })
  }

  // ── EXPORT UNLINKED POLICIES ─────────────────────────────────────────────
  // Exports unlinked transactions as a ready-to-import Policies Excel template.
  // User fills in Advisor 1 / % 1 columns then imports under Policies → Import.
  const exportUnlinkedPolicies = async () => {
    try {
      const { data: rows, error } = await supabase
        .from('policy_transactions')
        .select('policy_invoice_no, client, product, supplier')
        .is('policy_id', null)
      if (error) throw error

      // Group by policy_invoice_no — one row per unique policy
      const grouped = {}
      for (const tx of rows || []) {
        const key = (tx.policy_invoice_no || '').trim()
        if (!key) continue
        if (!grouped[key]) {
          grouped[key] = { policy_number: key, client: '', product: '', supplier: '', count: 0 }
        }
        grouped[key].count++
        if (!grouped[key].client && tx.client) grouped[key].client = tx.client
        if (!grouped[key].product && tx.product) grouped[key].product = tx.product
        if (!grouped[key].supplier && tx.supplier) grouped[key].supplier = tx.supplier
      }

      const sorted = Object.values(grouped).sort((a, b) => a.policy_number.localeCompare(b.policy_number))

      if (sorted.length === 0) {
        setToast({ message: 'No unlinked transactions found — all are linked!', type: 'success' })
        return
      }

      const XLSX = await import('xlsx')
      const wb = XLSX.utils.book_new()

      // Main sheet — one row per unique unlinked policy number
      const dataRows = sorted.map(p => ({
        'Policy Number': p.policy_number,
        'Customer Code': '',
        'Client': p.client,
        'Product Provider': p.supplier || '',
        'Category': '',
        'Product': p.product,
        'Start Date': '',
        'End Date': '',
        'Active': 'Yes',
        'Advisor 1': '',
        '% 1': '',
        'Advisor 2': '',
        '% 2': '',
        'Advisor 3': '',
        '% 3': '',
        'Tx Count': p.count,
      }))

      const ws = XLSX.utils.json_to_sheet(dataRows)
      const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'E6A83C' } } }
      const range = XLSX.utils.decode_range(ws['!ref'])
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })]
        if (cell) cell.s = headerStyle
      }
      ws['!cols'] = [
        { wch: 18 }, { wch: 14 }, { wch: 24 }, { wch: 20 }, { wch: 14 }, { wch: 20 },
        { wch: 12 }, { wch: 12 }, { wch: 8 },
        { wch: 20 }, { wch: 8 }, { wch: 20 }, { wch: 8 }, { wch: 20 }, { wch: 8 },
        { wch: 10 },
      ]
      XLSX.utils.book_append_sheet(wb, ws, 'Unlinked Policies')

      // Instructions tab
      const instrData = [
        { 'Instructions': '1. Fill in "Advisor 1" with the full advisor name exactly as it appears in SFP Wealth.' },
        { 'Instructions': '2. Fill in "% 1" with that advisor\'s commission split (e.g. 44 for 44%).' },
        { 'Instructions': '3. Add "Advisor 2" / "% 2" for joint policies. Splits should add up to 100.' },
        { 'Instructions': '4. Fill in Customer Code or Client name if missing.' },
        { 'Instructions': '5. Set Start Date and End Date if known (YYYY-MM-DD format).' },
        { 'Instructions': '6. Delete the "Tx Count" column before importing (it\'s for reference only).' },
        { 'Instructions': '7. Import this file under Policies → Import Policies.' },
        { 'Instructions': '8. After import, click "Re-link Unlinked" on the Policy Transactions page — transactions will auto-link.' },
      ]
      const wsInstr = XLSX.utils.json_to_sheet(instrData)
      wsInstr['!cols'] = [{ wch: 100 }]
      XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions')

      const dateStr = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `UnlinkedPolicies_${dateStr}.xlsx`)
      setToast({ message: `Exported ${sorted.length} unlinked policies`, type: 'success' })
    } catch (err) {
      setToast({ message: `Export failed: ${err.message}`, type: 'error' })
    }
  }

  // ── EXPORT ───────────────────────────────────────────────────────────────
  const handleExport = async () => {
    const XLSX = await import('xlsx')

    let rows = transactions
    if (exportScope === 'all') {
      const { data } = await supabase
        .from('policy_transactions')
        .select('*')
        .order('transaction_date', { ascending: false })
      rows = data || []
    }

    const exportData = rows.map(t => ({
      'Transaction Date': fmtDate(t.transaction_date),
      'Statement Date': fmtDate(t.statement_date),
      'Policy Number': t.policy_invoice_no || '',
      'Client': t.client || '',
      'Product': t.product || '',
      'Supplier': t.supplier || '',
      'Advisor Split %': t.advisor_split != null ? `${normPct(t.advisor_split)}%` : '',
      'Supplier Amount Excl': fmtR(t.supplier_amount_excl),
      'Supplier Amount VAT': fmtR(t.supplier_amount_vat),
      'Advisor Share Excl': fmtR(t.advisor_share_excl),
      'Advisor Share VAT': fmtR(t.advisor_share_vat),
      'Advisor Share Inc': fmtR(t.advisor_share_inc),
      'Active': t.active ? 'Yes' : 'No',
    }))

    const ws = XLSX.utils.json_to_sheet(exportData)

    const range = XLSX.utils.decode_range(ws['!ref'])
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })]
      if (cell) {
        cell.s = { font: { bold: true }, fill: { fgColor: { rgb: 'E6A83C' } } }
      }
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Policy Transactions')

    const advisorLabel = selectedAdvisor ? advisorName(selectedAdvisor) : 'All'
    const dateLabel = new Date().toISOString().slice(0, 10)
    XLSX.writeFile(wb, `PolicyTransactions_${advisorLabel}_${dateLabel}.xlsx`)
    setShowExportModal(false)
  }

  const handleSort = (key) => {
    setSort(prev => {
      const [prevKey, prevOrder] = prev.split(':')
      if (prevKey === key) return `${key}:${prevOrder === 'asc' ? 'desc' : 'asc'}`
      return `${key}:asc`
    })
    setPage(1)
  }

  const columns = [
    {
      key: 'import_date',
      label: 'Import Date',
      sortable: true,
      render: (row) => row.import_date
        ? <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>{fmtDate(row.import_date)}</span>
        : <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>—</span>
    },
    {
      key: 'import_sequence',
      label: 'Import #',
      sortable: true,
      render: (row) => row.import_sequence != null
        ? <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white text-sfp-orange' : 'bg-sfp-orange/10 text-sfp-orange'}`}>#{row.import_sequence}</span>
        : <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>—</span>
    },
    { key: 'transaction_date', label: 'Remittance Date', sortable: true, render: (row) => fmtDate(row.transaction_date) },
    { key: 'statement_date', label: 'Stmt Date', sortable: true, render: (row) => fmtDate(row.statement_date) },
    { key: 'policy_invoice_no', label: 'Policy Number', sortable: true, render: (row) => row.policy_invoice_no || '-' },
    { key: 'policy_id', label: 'Policy Linked', sortable: true, render: (row) => <PolicyLinkedBadge linked={!!row.policy_id} />, className: 'text-center' },
    { key: 'client', label: 'Client', sortable: true, render: (row) => row.client || '-' },
    { key: 'product', label: 'Product', sortable: true, render: (row) => row.product || '-' },
    { key: 'advisor_split', label: 'Split %', sortable: true, render: (row) => { const v = normPct(row.advisor_split); return v != null ? `${v}%` : '-' } },
    { key: 'supplier_amount_excl', label: 'Supplier Excl', sortable: true, render: (row) => fmtR(row.supplier_amount_excl) },
    { key: 'supplier_amount_vat', label: 'Supplier VAT', render: (row) => fmtR(row.supplier_amount_vat) },
    { key: 'advisor_share_excl', label: 'Adv Excl', sortable: true, render: (row) => fmtR(row.advisor_share_excl) },
    { key: 'advisor_share_vat', label: 'Adv VAT', render: (row) => fmtR(row.advisor_share_vat) },
    { key: 'advisor_share_inc', label: 'Adv Inc', sortable: true, render: (row) => fmtR(row.advisor_share_inc) },
    {
      key: 'active',
      label: 'Active',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggleActive(row.id, row.active) }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${row.active ? 'bg-green-500' : 'bg-gray-300'}`}
          title={row.active ? 'Click to deactivate' : 'Click to activate'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.active ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setEditingTx(row); setShowForm(true) }}
          className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
        >
          Edit
        </button>
      )
    },
  ]

  const tabClass = (t) =>
    `px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
      activeTab === t
        ? 'border-sfp-orange text-sfp-orange'
        : `border-transparent ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-sfp-darkgray'}`
    }`

  if (!canAccess('can_view_advisor_commissions')) {
    return (
      <div className="p-6 h-full flex flex-col items-center justify-center">
        <div className="text-center">
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>Access Denied</h3>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>You don't have permission to view commissions.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`p-4 h-full flex flex-col gap-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Hidden file input */}
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />

      {/* Action Bar */}
      <div className="flex justify-between items-center gap-4 flex-shrink-0 flex-wrap">
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="primary" onClick={() => { setEditingTx(null); setShowForm(true) }}>
            + Add Transaction
          </Button>
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>
            ↑ Import Excel
          </Button>
          <Button variant="secondary" onClick={downloadTemplate}>
            📋 Download Template
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                const { linked, stillUnlinked } = await relinkUnlinked()
                setToast({
                  message: `Re-linked ${linked} transaction${linked !== 1 ? 's' : ''} to policies${stillUnlinked > 0 ? ` (${stillUnlinked} still unmatched — no policy found)` : ''}`,
                  type: 'success'
                })
              } catch (err) {
                setToast({ message: `Re-link failed: ${err.message}`, type: 'error' })
              }
            }}
            title="Scan all unlinked transactions and match them to policies by policy number"
          >
            Re-link Unlinked
          </Button>
          <Button
            variant="secondary"
            onClick={exportUnlinkedPolicies}
            title="Export all unlinked transactions as a Policies import template (fill in advisor splits, then import under Policies)"
          >
            Export Unlinked Policies
          </Button>
          <Button variant="secondary" onClick={() => setShowExportModal(true)}>
            ↓ Export Excel
          </Button>

          {/* Advisor filter */}
          <select
            id="advisor-filter"
            name="advisor"
            value={selectedAdvisor?.id || ''}
            onChange={(e) => {
              const id = e.target.value
              setSelectedAdvisor(id ? advisors.find(a => a.id === id) || null : null)
              setPage(1)
            }}
            className="input-field text-sm"
          >
            <option value="">All Advisors</option>
            {advisors.map(a => (
              <option key={a.id} value={a.id}>{advisorName(a)}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <select
            id="sort-field"
            name="sort"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1) }}
            className="input-field text-sm"
          >
            <option value="import_date:desc">Import Date (Newest)</option>
            <option value="import_sequence:desc">Import # (Newest)</option>
            <option value="transaction_date:desc">Remittance Date (Newest)</option>
            <option value="transaction_date:asc">Remittance Date (Oldest)</option>
            <option value="statement_date:desc">Stmt Date (Newest)</option>
            <option value="client:asc">Client A → Z</option>
            <option value="policy_invoice_no:asc">Policy No. A → Z</option>
            <option value="advisor_share_inc:desc">Advisor Inc (High → Low)</option>
          </select>
          <input
            id="search-field"
            name="search"
            type="text"
            placeholder="Search policy, client, product..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="input-field max-w-xs"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-0 border-b flex-shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <button className={tabClass('transactions')} onClick={() => setActiveTab('transactions')}>
          Transactions {activeTab === 'transactions' && total > 0 ? `(${total})` : ''}
        </button>
        <button className={tabClass('batches')} onClick={() => setActiveTab('batches')}>
          Import Batches
        </button>
      </div>

      {activeTab === 'batches' ? (
        <ImportBatchesTab
          fetchBatches={fetchBatches}
          deleteBatches={deleteBatches}
          clearAllTransactions={clearAllTransactions}
          isDarkMode={isDarkMode}
        />
      ) : (
        <>
          {/* Advisor filter label */}
          {selectedAdvisor && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium flex-shrink-0 border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-amber-400' : 'bg-orange-50 border-sfp-orange text-sfp-orange'}`}>
              <span>Showing {total} {total === 1 ? 'transaction' : 'transactions'} for <strong>{advisorName(selectedAdvisor)}</strong></span>
              <button onClick={() => { setSelectedAdvisor(null); setPage(1) }} className="ml-auto text-xs underline hover:no-underline">
                Clear
              </button>
            </div>
          )}

          {/* Table */}
          <div className={`flex flex-col flex-1 rounded border transition-colors duration-300 min-h-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div className="overflow-auto flex-1">
              <Table
                columns={columns}
                data={transactions}
                loading={loading}
                onRowClick={(row) => { setEditingTx(row); setShowForm(true) }}
                isDarkMode={isDarkMode}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </div>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 space-y-2">
            <div className={`flex items-center justify-between px-4 py-2 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>
                Total Transactions: <span className="text-sfp-orange font-bold">{total}</span>
              </span>
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Showing {Math.min((page - 1) * 100 + 1, total)}-{Math.min(page * 100, total)} of {total}
              </span>
            </div>
            <Pagination currentPage={page} totalItems={total} pageSize={100} onPageChange={setPage} isDarkMode={isDarkMode} />
          </div>
        </>
      )}

      {/* Import Preview Modal */}
      {importStage === 'preview' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Import Preview</h3>

            <div className="space-y-3 mb-5">
              <div className="flex items-center gap-2 text-sm">
                <span className={`font-semibold ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Rows detected:</span>
                <span className="font-bold">{importRows.length}</span>
              </div>

              {importRows.length > 0 && (
                <div className={`text-xs rounded p-2 space-y-1 ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-blue-50 text-blue-700'}`}>
                  <p className="font-semibold">✓ Data quality check:</p>
                  <p>• {importRows.filter(r => r.transaction_date).length}/{importRows.length} have transaction date (rest will use today)</p>
                  <p>• {importRows.filter(r => r.policy_invoice_no).length}/{importRows.length} have policy number</p>
                  <p>• {importRows.filter(r => r.advisor_share_inc).length}/{importRows.length} have advisor share amount</p>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                <span>✓</span>
                <span><strong>{importRows.length}</strong> rows ready to import</span>
              </div>

              {importRows.length > 0 && (
                <div className="text-xs text-gray-500 mt-2">
                  <p className="font-semibold mb-1">Sample (first 3 rows):</p>
                  {importRows.slice(0, 3).map((r, i) => (
                    <p key={i} className="font-mono bg-gray-50 px-2 py-0.5 rounded mb-0.5 truncate">
                      {r.transaction_date || 'today'} | {r.policy_invoice_no} | {r.client} | {fmtR(r.advisor_share_inc)}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => { setImportStage(null); setImportRows([]) }} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={importRows.length === 0 || importStage === 'checking'}
                className="btn-primary"
              >
                {importStage === 'checking' ? 'Checking...' : `Import (${importRows.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Batch Warning */}
      {importStage === 'dup_batch' && dupBatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className="text-lg font-bold mb-2 text-amber-600">⚠️ Duplicate Batch Detected</h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              A batch with the same number of transactions and totals was already imported today
              at <strong>{fmtTime(dupBatch.imported_at)}</strong>.
            </p>
            <p className={`text-xs mb-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              This may be a duplicate import. You can proceed anyway or cancel.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setImportStage('preview'); setDupBatch(null) }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={doImport}
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {importStage === 'importing' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-8 text-center transition-colors duration-300 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="animate-spin h-8 w-8 border-4 border-sfp-orange border-t-transparent rounded-full mx-auto mb-3" />
            <p className={`font-semibold ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>Importing transactions...</p>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Export Options</h3>
            <div className="space-y-3 mb-5">
              {[
                { value: 'all', label: 'All transactions', desc: 'Export every transaction in DB' },
                { value: 'filtered', label: 'Filtered view', desc: `Current filter${selectedAdvisor ? `: ${advisorName(selectedAdvisor)}` : ''}${search ? `, "${search}"` : ''} (${total} rows)` },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportScope"
                    value={opt.value}
                    checked={exportScope === opt.value}
                    onChange={() => setExportScope(opt.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowExportModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleExport} className="btn-primary">Download</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <TransactionForm
          initialData={editingTx}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingTx(null) }}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}


    </div>
  )
}
